import { debounce } from '../../../utils/debounce'

export class Accordion {
	detailsEl: HTMLDetailsElement
	summaryEl: HTMLElement
	contentEl: HTMLElement
	isClosing: boolean
	isExpanding: boolean
	isSplit: boolean

	private resizeObserver: ResizeObserver | null = null
	private boundSummaryClickHandler = this.onClick.bind(this)
	private destroyed = false

	constructor(el: HTMLDetailsElement) {
		// Store the <details> element
		this.detailsEl = el
		// Store the <summary> element
		this.summaryEl = el.querySelector('summary')
		//Set initial height
		this.setHeight(`${this.summaryEl.offsetHeight}px`, false)
		// Store the <div class="content"> element
		this.contentEl = el.querySelector('.accordion-content')

		// deactivate the content interactivity by default
		this.contentEl.setAttribute('inert', '')

		// Store if the element is closing
		this.isClosing = false
		// Store if the element is expanding
		this.isExpanding = false
		//check if layout is split
		this.isSplit = el.parentElement.classList.contains('layout-split')
		// Detect user clicks on the summary element
		this.summaryEl.addEventListener('click', this.boundSummaryClickHandler)

		// Check if the element is already open
		if (this.detailsEl.attributes.getNamedItem('open') != null) {
			this.open()
		}

		// Add resize observer so we can watch for changes to the size of the summary and the content and
		// adjust the size of the accordion accordingly.  The content has to be watched as well as the
		// summary because the height set on open is only correct for the content that existed at that
		// moment; anything that arrives or grows afterwards - offers fetched into a block carousel,
		// images finishing their load, a nested component reflowing - would be clipped by the stale
		// height, since the details element is overflow: clip.
		this.resizeObserver = new ResizeObserver(() => {
			if (this.destroyed) return

			this.syncHeight()
		})
		this.resizeObserver.observe(this.summaryEl)
		this.resizeObserver.observe(this.contentEl)
	}

	/**
	 * Match the height to whatever the summary and content currently measure, for the state the
	 * accordion is in.  Writing the height can itself resize an observed element, so an unchanged
	 * value is left alone to keep the resize observer from looping.
	 */
	syncHeight(): void {
		const height = this.detailsEl.open
			? `${this.summaryEl.offsetHeight + this.contentEl.offsetHeight}px`
			: `${this.summaryEl.offsetHeight}px`

		this.setHeight(height, false)
	}

	/**
	 * Write a height onto the details element, with or without the CSS height transition.
	 *
	 * Corrections that come from the resize observer are applied without animating.  They happen
	 * for reasons the user did not ask for - content arriving, a reflow, or the accordion being
	 * revealed inside a tab panel that was display: none, where everything measured zero - and the
	 * transition would otherwise play those corrections as the accordion growing into place.  Only
	 * open() and shrink() animate.
	 */
	private setHeight(height: string, animate: boolean): void {
		if (this.detailsEl.style.height === height) return

		if (animate) {
			this.detailsEl.style.height = height
			return
		}

		// Override only transition-property so inline transition shorthand/longhands remain intact.
		const previousTransitionProperty = this.detailsEl.style.getPropertyValue('transition-property')
		const previousTransitionPropertyPriority = this.detailsEl.style.getPropertyPriority('transition-property')

		this.detailsEl.style.setProperty('transition-property', 'none', 'important')
		this.detailsEl.style.height = height
		// Flush the new height so restoring the transition cannot animate it.
		void this.detailsEl.offsetHeight

		if (previousTransitionProperty) {
			this.detailsEl.style.setProperty(
				'transition-property',
				previousTransitionProperty,
				previousTransitionPropertyPriority
			)
		} else {
			this.detailsEl.style.removeProperty('transition-property')
		}
	}

	/**
	 * Tear down listeners and observers so this instance can be garbage-collected.
	 */
	destroy(): void {
		if (this.destroyed) return
		this.destroyed = true

		this.summaryEl?.removeEventListener('click', this.boundSummaryClickHandler)
		this.resizeObserver?.disconnect()
		this.resizeObserver = null
	}

	onClick(e: MouseEvent) {
		if (this.destroyed) return

		// Stop default behaviour from the browser
		e.preventDefault()

		const target = e.target

		// If the click is on an element with a data-toast-* attribute or inside an element with a data-toast-* attribute,
		// do not toggle the accordion
		if (target instanceof HTMLElement && target.closest('[data-toast-uri],[data-toast-msg]')) {
			return
		}

		// Check if the element is being closed or is already closed
		if (this.isClosing || !this.detailsEl.open) {
			this.open()
			// Check if the element is being opened or is already open
		} else if (this.isExpanding || this.detailsEl.open) {
			this.shrink()
		}
	}

	shrink() {
		if (this.destroyed) return

		// Set the element as "being closed"
		this.isClosing = true

		// Calculate the height of the summary
		this.setHeight(`${this.summaryEl.offsetHeight}px`, true)

		this.onAnimationFinish(false)
	}

	close() {
		// Wait for the next frame to call the collapse function
		window.requestAnimationFrame(() => {
			if (this.destroyed) return
			this.shrink()
		})
	}

	open() {
		if (this.destroyed) return

		// Force the [open] attribute on the details element
		this.detailsEl.open = true
		//this.detailsEl.classList.add('show-content');
		// Set the element as "being expanding"
		this.isExpanding = true
		//offset content up into the summary level if layout is split
		if (this.isSplit) {
			this.contentEl.style.marginBlockStart = '-' + this.summaryEl.offsetHeight + 'px'
		}
		// Calculate the open height of the element (summary height + content height)
		const endHeight = `${this.summaryEl.offsetHeight + this.contentEl.offsetHeight}px`

		// Wait for the next frame to call the expand function
		window.requestAnimationFrame(() => {
			if (this.destroyed) return
			this.expand(endHeight)
		})
	}

	expand(endHeight: string) {
		if (this.destroyed) return

		this.setHeight(endHeight, true)

		this.onAnimationFinish(true)
	}

	onAnimationFinish(open: boolean) {
		if (this.destroyed) return

		// Set the open attribute based on the parameter
		this.detailsEl.open = open

		// toggle the content interactivity
		if (!open) {
			this.contentEl.setAttribute('inert', '')
		} else {
			this.contentEl.removeAttribute('inert')
		}

		// Reset isClosing & isExpanding
		this.isClosing = false
		this.isExpanding = false
	}
}

export class GMXAccordion extends HTMLElement {
	state: string
	originalHTML: string

	private accordionInstances: Accordion[] = []
	private boundWindowResizeHandler = debounce(this._handleWindowResize.bind(this), 100)

	connectedCallback(): void {
		this._initAccordions()

		// We only need this with the footer variant
		if (this.classList.contains('layout-footer')) {
			//save the HTML for later
			this.originalHTML = this.innerHTML
			//Need to set our initial state
			if (this.isMobileOrTablet()) {
				this.state = 'mobile'
			} else {
				this._switchMobileToDesktop()
			}

			window.addEventListener('resize', this.boundWindowResizeHandler)
		}
	}

	disconnectedCallback(): void {
		this._destroyAccordions()
		window.removeEventListener('resize', this.boundWindowResizeHandler)
	}

	private _initAccordions(): void {
		this._destroyAccordions()

		this.querySelectorAll('details').forEach(el => {
			this.accordionInstances.push(new Accordion(el))
		})
	}

	private _destroyAccordions(): void {
		this.accordionInstances.forEach(instance => instance.destroy())
		this.accordionInstances = []
	}

	private _switchMobileToDesktop(): void {
		this.state = 'desktop'

		// Tear down mobile accordion instances before replacing markup; otherwise
		// ResizeObservers and click listeners keep detached nodes alive.
		this._destroyAccordions()

		this.querySelectorAll('details').forEach((el: HTMLElement) => {
			el.classList.add('details')
			el.style.height = ''
		})

		this.querySelectorAll('summary').forEach((el: HTMLElement) => {
			el.classList.add('summary')
		})

		// remove inert attribute to make inner links accessible
		this.querySelectorAll('.accordion-content').forEach((el: HTMLElement) => {
			el.removeAttribute('inert')
		})

		//let compHTML: string = this.originalHTML;
		this.innerHTML = this.innerHTML
			.replaceAll('<summary', '<div')
			.replaceAll('</summary>', '</div>')
			.replaceAll('<details', '<div')
			.replaceAll('</details>', '</div>')

		//this.innerHTML = compHTML;
	}

	private _switchDesktopToMobile(): void {
		this.state = 'mobile'
		this.innerHTML = this.originalHTML
		this._initAccordions()
	}

	/**
	 * Callback for handling window resize.  Need to make accessible or not.
	 * @private
	 */
	private _handleWindowResize(): void {
		if (!this.isMobileOrTablet() && this.state === 'mobile') {
			this._switchMobileToDesktop()
		} else {
			if (this.isMobileOrTablet() && this.state === 'desktop') {
				this._switchDesktopToMobile()
			}
		}
	}

	/**
	 * Check if the screen is mobile size
	 */
	isMobileOrTablet(): boolean {
		return !window.matchMedia('screen and (min-width: 900px)').matches
	}
}

if (!customElements.get('gmx-accordion')) {
	customElements.define('gmx-accordion', GMXAccordion)
}
